/**
 * Build 6B — the figures nobody could enter from a screen: KPI targets, a
 * surveyed condition score, funding envelopes and test thresholds.
 *
 * The shapes the forms send and read back, and the checks the platform makes,
 * in the platform's own words. The mock source refuses with these same
 * functions, so the demonstration says no to exactly what the platform does
 * (insight/targets.py, routers/asset_management.py `record_condition`,
 * routers/liability.py `put_envelope`, testing/records.py `set_thresholds`).
 *
 * Pure: no React, no data source.
 */

export type TargetStatus = "proposed" | "agreed";

export interface KpiTargetInput {
  metricKey: string;
  target: number;
  status: TargetStatus;
  basis: string;
}

export interface KpiTargetChange {
  action: "set" | "retired";
  target: number | null;
  status: TargetStatus | null;
  basis: string | null;
  reason: string | null;
  scope: "site" | "tenant";
  by: string | null;
  at: string;
}

export interface AmConditionInput {
  score: number;
  /** yyyy-mm-dd; today when omitted */
  surveyedOn?: string;
  basis: string;
}

export interface AmConditionResult {
  id: string;
  tag: string;
  conditionScore: number;
  conditionAt: string;
  conditionBasis: string;
  healthScore: number | null;
  healthBand: number | null;
}

export interface AmSurvey {
  id: string;
  tag: string;
  name: string;
  conditionScore: number;
  conditionAt: string | null;
  conditionBasis: string | null;
  conditionBy: string | null;
  healthScore: number | null;
  healthBand: number | null;
}

export type EnvelopeKind = "capex" | "opex";

export interface FundingEnvelope {
  id: string;
  fiscalYear: number;
  kind: EnvelopeKind;
  amount: number;
  currency: string;
  /** what the figure rests on — the board budget, the approved plan */
  label: string | null;
  scope: "site" | "tenant";
  /** for a year with both, the site's own applies */
  applies: boolean;
  setBy: string | null;
  setAt: string | null;
}

export interface EnvelopeInput {
  fiscalYear: number;
  kind: EnvelopeKind;
  amount: number;
  currency: string;
  label?: string;
}

export interface ThresholdLevels {
  label: string;
  attention: number | null;
  fail: number | null;
  higherIsBetter: boolean;
}

export interface ThresholdsInput {
  /** the type's thresholds in full, each with its new levels; omitted to change only the interval */
  thresholds?: Record<string, unknown>[];
  intervalDays?: number;
  status: TargetStatus;
  basis: string;
}

/** A threshold as the platform stores it, with new levels; everything else it carries is kept. */
export function thresholdToWire(
  th: {
    label: string;
    match?: Record<string, string> | null;
    basis?: "value" | "pct_of_specified";
    higherIsBetter?: boolean;
  },
  levels: { attention: number | null; fail: number | null },
): Record<string, unknown> {
  return {
    label: th.label,
    match: th.match ?? null,
    basis: th.basis ?? "value",
    ...(th.higherIsBetter === false ? { higher_is_better: false } : {}),
    attention: levels.attention,
    fail: levels.fail,
  };
}

/* ── the platform's refusals ─────────────────────────────────────────── */

const finite = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n);

/** insight/targets.py `set_target`. */
export function kpiTargetRefusal(input: KpiTargetInput, knownKeys: readonly string[]): string | null {
  if (input.status !== "proposed" && input.status !== "agreed") return "status must be one of proposed, agreed";
  const basis = input.basis.trim();
  if (!basis) return "basis is required — say what the target was taken from";
  if (basis.length > 500) return "basis is too long — keep it under 500 characters";
  if (!finite(input.target)) return "target must be a number";
  if (!knownKeys.includes(input.metricKey)) return `no installed measure '${input.metricKey}' for this tenant`;
  return null;
}

/** insight/targets.py `retire_target`. */
export function retireRefusal(reason: string): string | null {
  return reason.trim() ? null : "say why the target is retired";
}

export const NO_SITE_TARGET = "there is no target of this site's own to retire for that measure";

/** routers/asset_management.py `record_condition`. */
export function conditionRefusal(input: AmConditionInput, today: string): string | null {
  if (!finite(input.score) || input.score < 0 || input.score > 100)
    return "a condition score is a number from 0 to 100";
  const basis = input.basis.trim();
  if (!basis) return "say what the score rests on — the survey, inspection or method";
  if (basis.length > 500) return "the basis is too long — keep it under 500 characters";
  if (input.surveyedOn && input.surveyedOn > today) return "the survey date is in the future";
  return null;
}

/** routers/liability.py `put_envelope` (and its model's bounds). */
export function envelopeRefusal(input: EnvelopeInput): string | null {
  if (input.kind !== "capex" && input.kind !== "opex") return "kind must be capex or opex";
  if (!Number.isInteger(input.fiscalYear) || input.fiscalYear < 2000 || input.fiscalYear > 2100)
    return "the fiscal year must be between 2000 and 2100";
  if (!finite(input.amount)) return "the amount must be a number";
  if (input.amount < 0) return "the amount cannot be negative";
  if (!/^[A-Za-z]{3}$/.test(input.currency.trim())) return "the currency is a three-letter code, such as SAR";
  if ((input.label ?? "").trim().length > 200) return "the basis is too long — keep it under 200 characters";
  return null;
}

/** routers/asset_management.py `edit_plan` (and its model's bounds). */
export function planPatchRefusal(
  trigger: "calendar" | "usage",
  patch: { intervalDays?: number; nextDueOn?: string | null; title?: string },
): string | null {
  if (patch.intervalDays !== undefined) {
    if (trigger !== "calendar") return "this plan runs on usage, so it has no interval in days";
    if (!Number.isInteger(patch.intervalDays) || patch.intervalDays < 1 || patch.intervalDays > 3650)
      return "the interval is a number of days from 1 to 3650";
  }
  if (patch.nextDueOn && !/^\d{4}-\d{2}-\d{2}$/.test(patch.nextDueOn)) return "the due date is not a date";
  if (patch.title !== undefined && (patch.title.trim().length < 3 || patch.title.length > 200))
    return "a plan title is 3 to 200 characters";
  return null;
}

/** testing/records.py `set_thresholds` and routers/testing.py. */
export function thresholdsRefusal(input: ThresholdsInput): string | null {
  if (input.status !== "proposed" && input.status !== "agreed") return "status must be proposed or agreed";
  if (!input.basis.trim()) return "say what the values were confirmed against";
  if (
    input.intervalDays !== undefined &&
    (!Number.isInteger(input.intervalDays) || input.intervalDays < 1 || input.intervalDays > 3650)
  )
    return "the interval is a number of days from 1 to 3650";
  for (const t of input.thresholds ?? []) {
    const label = String(t.label ?? "");
    const fail = t.fail;
    const attention = t.attention;
    const isBlank = (v: unknown) => v === null || v === undefined || v === "";
    if (isBlank(fail) && isBlank(attention)) return `threshold '${label}' has neither an attention nor a fail level`;
    for (const [name, v] of [
      ["fail", fail],
      ["attention", attention],
    ] as const)
      if (!isBlank(v) && !finite(v)) return `threshold '${label}': the ${name} level must be a number`;
    if (finite(fail) && finite(attention)) {
      const higher = t.higher_is_better !== false;
      if ((higher && attention < fail) || (!higher && attention > fail))
        return `threshold '${label}': the attention level must sit between pass and fail — ${higher ? "at or above" : "at or below"} the fail level`;
    }
  }
  return null;
}

/**
 * What a refusal says, for the screen: the platform's sentence without the
 * transport's prefix ("400: …", "Not found: …"). A permission refusal is
 * left as it is, because "Not permitted" is the useful part.
 */
export function refusalText(e: unknown): string {
  const status = (e as { status?: number } | null)?.status;
  const detail = (e as { detail?: unknown } | null)?.detail;
  if (status !== 403 && typeof detail === "string" && detail) return detail;
  return e instanceof Error ? e.message : String(e);
}
